<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('ADD_BLACKLIST')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="blackListRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="blackListData"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('REMOVE_BLACKLIST')"
              @click="onRemoveItem(data.item, $event.target)"
            >
              <feather-icon icon="DeleteIcon" />
              <span class="align-middle ml-50"> {{ $t('Delete') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      :ok-disabled="isLoading"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="blackListRules">
        <b-form>
          <b-row class="match-height">
            <!-- Bank Code -->
            <b-col md="6">
              <SelectField
                id="bank-code"
                v-model="blackListFormData.bankCode"
                :label="$t('Bank Code')"
                :placeholder="$t('Enter bank code')"
                :options="bankListOptions"
                :reduce="option => option"
                item="label"
              />
            </b-col>
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="blackListFormData.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter account number')"
                type="text"
                :error-name="$t('Account Number')"
                rules="required"
                for="name"
                :readonly="isReadonly"
                @input="performNameInquiry"
              />
            </b-col>
            <!-- Account Number -->
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="blackListFormData.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter account name')"
                type="text"
                for="name"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Account Name -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
      <FullScreenSpinner :show="showSpinner" />
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchBlackList"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <!-- Bank Code -->
        <b-col md="6">
          <InputField
            id="bank-code"
            v-model="filter.bankCode"
            :label="$t('Bank Code')"
            :placeholder="$t('Enter bank code')"
            type="text"
          />
        </b-col>
        <!-- Bank Code -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BModal,
  BForm,
  BDropdownItem,
  BDropdown,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { fraudMonitorPermissions } from '@/helpers/permissions'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import Button from '../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    SelectField,
    BTable,
    BModal,
    BForm,
    BSpinner,
    ValidationObserver,
    InputField,
    BDropdownItem,
    BDropdown,
    TablePagination,
    Button,
    TableEntries,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      query: '',
      isReadonly: false,
      isEditable: false,
      showSpinner: false,
      sortBy: 'name',
      buttonLabel: this.$t('Save'),
      required,
      modalTitle: this.$t('Create Blacklist'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'bankCode', sortable: true, label: 'Bank Code' },
        { key: 'bankName', sortable: true, label: 'Bank Name' },
        { key: 'actions', label: 'Actions' },
      ],
      blackListFormData: {
        accountName: '',
        accountNumber: '',
        bankCode: '',
        bankName: '',
        id: '',
      },
      filter: {
        accountNumber: '',
        bankCode: '',
      },
      blackListData: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    ...mapGetters({
      bankListOptions: 'eChannels/allBankOptions',
    }),
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchBlackList()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchBlackList()
    },
  },
  mounted() {
    this.fetchBlackList()
  },
  methods: {
    ...mapActions({
      getBlackList: 'fraudmonitor/getBlackListAction',
      createBlackList: 'fraudmonitor/createBlackListAction',
      removeBlackListItem: 'fraudmonitor/removeBlackListItemAction',
      getBankNameByAccountNumber: 'eChannels/getBankNameByAccountNumber',
    }),
    async performNameInquiry(accountNumber) {
      if (this.blackListFormData.bankCode.value && accountNumber.length === 10) {
        try {
          this.showSpinner = true
          const formData = {
            destinationInstitutionCode: this.blackListFormData.bankCode.value,
            accountNumber,
            sourceAccountNumber: accountNumber,
            sourceAccountName: '',
            amount: 0,
            channelCode: '2',
            clientRef: uuidv4(),
          }
          const response = await this.getBankNameByAccountNumber({
            formData, permission: fraudMonitorPermissions.ADD_BLACKLIST,
          })
          const { status, data } = response
          if (status === 200 && data) {
            this.blackListFormData.accountName = data.accountName
            this.showSpinner = false
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
    },
    async onRemoveItem(items) {
      const { id } = items
      const permission = fraudMonitorPermissions.VIEW_BLACKLIST
      const deleteBlackListItem = async () => {
        try {
          this.showSpinner = true
          const response = await this.removeBlackListItem({ id, permission })
          const { status } = response
          if (status === 200) {
            this.fetchBlackList()
            this.alertSuccess({ text: this.$t('Your request to remove from Blacklist was successful') })
            this.showSpinner = false
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(deleteBlackListItem, { title: '', text: this.$t('Are you sure you want to Remove from Blacklist?') })
    },
    async fetchBlackList() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getBlackList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: fraudMonitorPermissions.VIEW_BLACKLIST,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.blackListData = data.data
          this.totalCount = data.recordCount
        } else {
          this.blackListData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async blackListAction() {
      try {
        this.isLoading = true
        this.showSpinner = true
        const formData = {
          ...this.blackListFormData,
          bankCode: this.blackListFormData.bankCode.value,
          bankName: this.blackListFormData.bankCode.label,
        }
        const response = await this.createBlackList({ formData, permission: fraudMonitorPermissions.ADD_BLACKLIST })
        const { status } = await response
        if (status === 200) {
          this.fetchBlackList()
          this.hideModal('modal-lg')
          this.alertSuccess({
            text: this.$t('Your request to Add Blacklist was successful'),
          })
          this.showSpinner = false
        }
      } catch (error) {
        this.hideModal('modal-lg')
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to Add Blacklist failed. Reason:')
          this.alertError({ text: `${reasonText} ${responseError.errorMessage}` })
        })
      } finally {
        this.hideModal('modal-lg')
        this.showSpinner = false
      }
    },
    async handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.blackListRules.validate().then(async success => {
          if (success) {
            this.confirm(this.blackListAction, {
              text: this.$t('Are you sure you want to Add to Blacklist?'),
            })
          }
        })
      } catch (error) {
        this.hideModal('modal-lg')
        this.alertError({ text: error })
      } finally {
        this.hideModal('modal-lg')
      }
    },
    resetModal() {
      this.blackListFormData = { ...null }
      this.isEditable = false
      this.isLoading = false
      this.isReadonly = false
      this.modalTitle = this.$t('Create Blacklist')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchBlackList()
    },
  },
}
</script>
